import { useRef, useEffect, useLayoutEffect, useState, useCallback, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Banner } from "../../types";
import { useAllCityTranslations } from "../../hooks/useBusinessData";
import { MessageCircleMore } from "lucide-react";
import { useBannerFontGroups, getBannerSize } from "../../hooks/useBannerFonts";
import { isLoggedIn as isLoggedInFn } from "../../hooks/useAuth";
import { shouldIgnoreClick } from "../../utils/clickGuard";
import { bannerImgAttrs, CTX_SIZES } from "../../utils/bannerImg";
import PendingApprovalOverlay from "../ui/PendingApprovalOverlay";

interface StandardGridProps {
  banners: Banner[];
  showCity?: boolean;
  showCategory?: boolean;
  aspectRatio?: string;
  // 무한 로드 모드 (대량 실버 전용): 끝 도달 시 loadMore (페이지네이션). 카드 이미지는 VirtualCard 가 화면 밖에서 해제.
  virtuoso?: boolean;
  loadMore?: () => void;
  scrollParent?: HTMLElement | null;
}

// 가상 스크롤 + lazy mount — 각 카드가 자기 visibility 추적, 보일 때만 img/텍스트 mount.
// 안 보이면 placeholder (height 유지 → layout 안정). unmount 시 img 해제로 메모리 회수.
// memo: 페이지네이션으로 항목이 누적될 때(30→60→…) 기존 카드는 props 동일 → 리렌더 스킵 (스크롤 jank 방지)
const VirtualCard = memo(function VirtualCard({
  item, idx, showCity, showCategory, cityNames, fontIsPc, fontGroups, onSelect, forceVisible,
}: {
  item: Banner;
  idx: number;
  showCity: boolean;
  showCategory: boolean;
  cityNames: Record<string, string>;
  fontIsPc: boolean;
  fontGroups: ReturnType<typeof useBannerFontGroups>["groups"];
  onSelect: (e: React.MouseEvent, item: Banner) => void;
  forceVisible?: boolean; // 윈도잉 모드: 상위가 보이는 범위만 렌더하므로 자체 IO 불필요, 즉시 표시
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // 한 번 보이면 유지 (toggle 시 빠른 스크롤에서 mount/unmount thrash + 이미지 재디코딩 → 페인트 jank 악화).
  // 누적 문제는 상위 컴포넌트의 윈도잉(보이는 범위만 렌더)으로 해결한다.
  const [visible, setVisible] = useState(idx < 15 || !!forceVisible);

  useEffect(() => {
    if (forceVisible) return; // 윈도잉이 가시성 관리 → IO 불필요
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: "600px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [forceVisible]);

  // unmount 시 img.src 비우기 — 디코딩된 비트맵 메모리 회수
  useEffect(() => {
    return () => {
      const img = imgRef.current;
      if (img) { try { img.removeAttribute("src"); img.removeAttribute("srcset"); } catch { /* */ } }
    };
  }, []);

  if (!item) return null;

  const showCi = showCity && !!item.showCity && !!item.location;
  const showCat = showCategory && !!item.categoryKey;
  const showT = !!item.showTitle;
  const showS = !!item.showSubtitle && !!item.subtitle;
  const showCnt = !!item.showCommentCount;
  const showFav = !!item.showFavoriteCount;
  const hasLine2 = showS || showCi || showCat || showCnt || showFav;
  const { titleSize, subtitleSize } = getBannerSize(item.tier, fontIsPc, fontGroups);

  return (
    <button
      ref={ref}
      data-banner-card
      onClick={(e) => { if (item.isConfirmed === false) { e.preventDefault(); return; } onSelect(e, item); }}
      aria-disabled={item.isConfirmed === false}
      className={`flex w-full flex-col self-start bg-white text-left transition-all ${item.isConfirmed === false ? "cursor-not-allowed" : "hover:-translate-y-1"}`}
    >
      {/* 이미지 영역 — 높이는 항상 16/9 로 고정 → 가상화 시 layout shift 없음 */}
      <div
        className="relative block w-full shrink-0 overflow-hidden bg-slate-100 rounded-md"
        style={{ aspectRatio: "16/9" }}
      >
        {visible && (
          <>
            <img
              ref={imgRef}
              {...bannerImgAttrs(item.imageUrl, item.imageSizes, CTX_SIZES.card, fontIsPc ? 200 : 80)}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
              fetchPriority={idx < 15 ? "high" : "auto"}
              className="absolute inset-0 h-full w-full object-cover"
            />
            {showT && item.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-6 pb-1 z-10">
                <div
                  className="font-bold text-white text-center truncate leading-tight"
                  style={{ fontSize: titleSize }}
                >
                  {item.title}
                </div>
              </div>
            )}
            {!item.isConfirmed && <PendingApprovalOverlay />}
          </>
        )}
      </div>
      {/* 정보 영역 — 메타 한 줄 (가벼움, 항상 렌더) */}
      {hasLine2 && visible && (
        <div className="bg-white px-1.5 pt-0.5 pb-1 shrink-0">
          <div
            className="flex items-center gap-1 text-black min-w-0 leading-tight min-h-[16px]"
            style={{ fontSize: subtitleSize }}
          >
            {showCi && (<span className="shrink-0 text-black">{cityNames[item.location] || item.location}</span>)}
            {showS && (<span className="flex-1 min-w-0 truncate">{item.subtitle}</span>)}
            {showCat && (<span className="shrink-0 text-black">{item.categoryKey}</span>)}
            {showCnt && (
              <span className="shrink-0 inline-flex items-center gap-0.5 text-rose-600 font-bold tabular-nums">
                <MessageCircleMore className="h-3.5 w-3.5" />
                {(item.commentCount || 0).toLocaleString()}
              </span>
            )}
            {showFav && (<span className="shrink-0 text-rose-600 font-bold tabular-nums">❤️ {(item.favoriteCount || 0).toLocaleString()}</span>)}
          </div>
        </div>
      )}
      {/* line2 안 보이는 카드도 placeholder 영역 유지 (layout 안정) */}
      {hasLine2 && !visible && <div className="bg-white pt-0.5 pb-1 min-h-[20px]" />}
    </button>
  );
}, (prev, next) =>
  // item 은 페이지 누적 시 useMemo(map(toBanner)) 로 매번 새 객체가 됨 → 참조 비교로는 항상 리렌더.
  // 고유키(advertiserKey) + 표시 관련 안정 props 만 비교해, 내용 동일하면 리렌더 스킵 (스크롤 jank 제거)
  prev.item.advertiserKey === next.item.advertiserKey &&
  prev.item.imageUrl === next.item.imageUrl &&
  prev.item.title === next.item.title &&
  prev.item.subtitle === next.item.subtitle &&
  prev.item.commentCount === next.item.commentCount &&
  prev.item.isConfirmed === next.item.isConfirmed &&
  prev.idx === next.idx &&
  prev.showCity === next.showCity &&
  prev.showCategory === next.showCategory &&
  prev.cityNames === next.cityNames &&
  prev.fontIsPc === next.fontIsPc &&
  prev.fontGroups === next.fontGroups &&
  prev.onSelect === next.onSelect
);

const GRID_CLASS = "grid items-start grid-cols-3 gap-[7.5px] px-[3.75px] sm:grid-cols-5 sm:gap-[15px] sm:px-[7.5px]";

interface CardCommon {
  showCity: boolean;
  showCategory: boolean;
  cityNames: Record<string, string>;
  fontIsPc: boolean;
  fontGroups: ReturnType<typeof useBannerFontGroups>["groups"];
  onSelect: (e: React.MouseEvent, item: Banner) => void;
}

/**
 * 진짜 DOM 윈도잉 — scrollParent(메인 스크롤 컨테이너) 기준 보이는 행 ± 버퍼만 카드 렌더.
 * 위/아래는 spacer div 로 전체 높이를 유지해 스크롤바·위치 정상. 화면 밖 카드는 DOM 에서 제거 → 노드/이미지 누적 0.
 * 끝 근처 도달 시 loadMore (페이지네이션).
 */
function WindowedGrid({ banners, scrollParent, loadMore, common }: {
  banners: Banner[]; scrollParent?: HTMLElement | null; loadMore?: () => void; common: CardCommon;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rowH, setRowH] = useState(0);
  const [range, setRange] = useState({ start: 0, end: 30 });
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const colsRef = useRef(typeof window !== "undefined" && window.innerWidth >= 640 ? 5 : 3);
  const gap = colsRef.current === 5 ? 15 : 7.5;

  // 첫 카드 높이 측정 → 행 높이 (행 단위 윈도잉 계산용)
  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const card = c.querySelector("[data-banner-card]") as HTMLElement | null;
    if (card && card.offsetHeight) {
      const h = card.offsetHeight + gap;
      setRowH((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    }
  });

  useEffect(() => {
    const sp = scrollParent;
    if (!sp) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const c = containerRef.current;
      if (!c) return;
      const cols = colsRef.current;
      const rh = rowH || 220;
      const cRect = c.getBoundingClientRect();
      const spRect = sp.getBoundingClientRect();
      const relTop = cRect.top - spRect.top; // 그리드 상단의 뷰포트 상대 위치(스크롤로 음수)
      const buf = 8; // 위/아래 버퍼 행 — 넉넉히 두어 스크롤/탭전환 시 이미지 mount/unmount churn(깜빡임) 감소
      const firstRow = Math.max(0, Math.floor(-relTop / rh) - buf);
      const lastRow = Math.ceil((-relTop + spRect.height) / rh) + buf;
      const start = firstRow * cols;
      const end = Math.min(banners.length, lastRow * cols);
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
      if (lastRow * cols >= banners.length - cols * 3) loadMoreRef.current?.();
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    sp.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    compute();
    return () => {
      sp.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollParent, rowH, banners.length]);

  const cols = colsRef.current;
  const totalRows = Math.ceil(banners.length / cols);
  const startRow = Math.floor(range.start / cols);
  const endRow = Math.ceil(range.end / cols);
  const topPad = startRow * rowH;
  const botPad = Math.max(0, (totalRows - endRow) * rowH);
  const slice = banners.slice(range.start, range.end);

  return (
    <div ref={containerRef}>
      {topPad > 0 && <div style={{ height: topPad }} aria-hidden />}
      <div className={GRID_CLASS}>
        {slice.map((item, i) => (
          <VirtualCard key={item.advertiserKey} item={item} idx={range.start + i} forceVisible {...common} />
        ))}
      </div>
      {botPad > 0 && <div style={{ height: botPad }} aria-hidden />}
    </div>
  );
}

export default function StandardGrid({ banners, showCity = true, showCategory = true, virtuoso = false, loadMore, scrollParent }: StandardGridProps) {
  const cityNames = useAllCityTranslations();
  const { groups: fontGroups, isPc: fontIsPc } = useBannerFontGroups();
  const navigate = useNavigate();
  const location = useLocation();

  // #449 3차: 팝업 state 대신 /banner/:id 라우트(background location 패턴)로 오픈
  const handleSelect = useCallback((e: React.MouseEvent, item: Banner) => {
    if (shouldIgnoreClick()) { e.preventDefault(); return; }
    if (!isLoggedInFn()) {
      window.dispatchEvent(new Event("require-login"));
      return;
    }
    const m = item.advertiserKey.match(/^banner-(\d+)$/);
    if (!m) return;
    navigate(`/banner/${m[1]}`, { state: { backgroundLocation: location } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location]);

  const common: CardCommon = { showCity, showCategory, cityNames, fontIsPc, fontGroups, onSelect: handleSelect };

  return (
    <>
      {virtuoso ? (
        // 대량 실버: 진짜 DOM 윈도잉 (보이는 범위만 렌더 → 노드/이미지 누적 0, 스크롤 jank 해소)
        <WindowedGrid banners={banners} scrollParent={scrollParent} loadMore={loadMore} common={common} />
      ) : (
        <div className={GRID_CLASS}>
          {banners.map((item, idx) => (
            <VirtualCard key={item.advertiserKey} item={item} idx={idx} {...common} />
          ))}
        </div>
      )}
    </>
  );
}
